import classNames from "classnames";
import groupBy from "lodash/groupBy";
import { ActionIcon } from "PFComponents/action_icon";
import { Inline } from "PFComponents/containers";
import { SelectPill } from "PFComponents/double_section_pill/select_pill";
import { Select } from "PFComponents/select";
import { Typography } from "PFComponents/typography";
import { canAddMatchableTypeValue, canEditOrRemoveMatchableTypeValues } from "PFCore/helpers/activities";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { fetchAutocompleteCustomValues } from "PFCore/services/autocomplete/fetch_autocomplete_custom_values";
import { NormalizedKeyword } from "PFReducers/search_reducer/helpers/normalize_keywords";
import { CustomType, Id, Importance, Option, ParsedSchemaProperty, PureCustomValueCamelCased } from "PFTypes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./keywords_sliders.module.scss";

type ImportanceOption = {
  id: Importance;
  value: string;
  original: Importance;
  displayElement: string;
};

type CustomValueOption = Omit<PureCustomValueCamelCased, "type" | "value"> & {
  type: CustomType;
  value: string | boolean;
};

type TypeSectionProps = {
  remove?: (keyword: NormalizedKeyword) => void;
  handleChange?: (option: Option<CustomValueOption>) => void;
  search?: boolean;
  pushWithNewImportance: (keyword: NormalizedKeyword, importance: Importance) => void;
  type: CustomType;
  keywords: NormalizedKeyword[];
  error?: string;
  initialOpen?: boolean;
  importanceOptions: ImportanceOption[];
  onChangeOpen?: (open: boolean) => void;
};

const TypeSection = ({
  remove,
  handleChange,
  search,
  pushWithNewImportance,
  type,
  keywords,
  error,
  initialOpen = false,
  importanceOptions,
  onChangeOpen
}: TypeSectionProps) => {
  const { t } = useTranslation("search");
  const [open, setOpen] = useState(initialOpen);

  const notChosen = (option: { id: Id }) => !keywords.find(({ id }) => id === option.id);

  const handleOpen = (open: boolean) => {
    setOpen(open);
    onChangeOpen?.(open);
  };

  return (
    <li
      onClick={open ? undefined : () => handleOpen(true)}
      onKeyDown={open ? undefined : () => handleOpen(true)}
      className={classNames(css.section, { [css.sectionClosed]: !open, [css.error]: error })}
    >
      <Inline justifyContent="space-between" alignItems="center">
        <Typography variant="bodyRegular">
          {type.display_as} ({keywords.length}) {!search && ` - ${t("parts.keywords.editOriginalWarn")}`}
        </Typography>
        <ActionIcon
          size="lg"
          name={open ? "cross" : "add"}
          color="palettePrimary2"
          onClick={() => handleOpen(!open)}
        />
      </Inline>
      {error && <span className={css.errorMessage}>{error}</span>}
      {open && (
        <section className={css.sectionList}>
          {handleChange && (
            <Select<CustomValueOption>
              fetchOptions={({ searchTerm: term }) => {
                if (!term) {
                  return Promise.resolve({ entries: [], meta: { total: 0 } });
                }
                return fetchAutocompleteCustomValues({ type: type.name, ...(term ? { term } : {}) }).then(
                  (options) => ({
                    entries: options.map(
                      (option): Option<CustomValueOption> => ({
                        id: option.id,
                        value: String(option.text),
                        original: { ...option, type, value: option.text }
                      })
                    ),
                    meta: { total: options.length }
                  })
                );
              }}
              parseOptions={(options) => options.filter(notChosen) as Option<CustomValueOption>[]}
              onChange={handleChange}
              placeholder={t("parts.keywords.addAttribute")}
            />
          )}
          <div className={css.pillsList}>
            {keywords.map((keyword) => (
              <SelectPill
                key={keyword.id}
                leftContent={String(keyword.value)}
                value={keyword.importance}
                options={importanceOptions}
                onAttributeChange={(newImportance) => pushWithNewImportance(keyword, newImportance)}
                onDelete={remove ? () => remove(keyword) : undefined}
              />
            ))}
          </div>
        </section>
      )}
    </li>
  );
};

type KeywordsSlidersProps = {
  remove?: (keyword: NormalizedKeyword) => void;
  search?: boolean;
  pushWithNewImportance: (keyword: NormalizedKeyword, importance: Importance) => void;
  handleAddOne: (payload: { text: string } & Record<string, unknown>) => void;
  choosenKeywords: NormalizedKeyword[];
  matchableTypes: CustomType[];
  style?: React.CSSProperties;
  errors?: Record<string, string>;
  typesOpened?: string[];
  onChangeOpen?: (open: boolean, typeName: string) => void;
};

export const KeywordsSliders = ({
  remove,
  search,
  pushWithNewImportance,
  handleAddOne,
  choosenKeywords,
  matchableTypes,
  style,
  errors,
  typesOpened = [],
  onChangeOpen
}: KeywordsSlidersProps) => {
  const { customTypes } = useCustomTypes();
  const { t } = useTranslation("search");

  const matchableTypeNames = matchableTypes.map((type) => type.name);

  const keywords = choosenKeywords.filter(({ type }) => matchableTypeNames.includes(type.name));

  const notChosen = (option: { id: Id }) => !keywords.find(({ id }) => id === option.id);

  const keywordsByType = groupBy(keywords, (keyword) => keyword.type.id);
  const typesToDisplay = customTypes
    .filter((type) => matchableTypeNames.includes(type.name))
    .sort((typeA, typeB) => ((typeA.display_as ?? "") > (typeB.display_as ?? "") ? 1 : -1));

  const importanceOptions = useMemo(
    () => [
      {
        id: Importance.Supporting,
        value: t("parts.keywords.supporting"),
        original: Importance.Supporting,
        displayElement: t("parts.keywords.supporting")
      },
      {
        id: Importance.Essential,
        value: t("parts.keywords.essential"),
        original: Importance.Essential,
        displayElement: t("parts.keywords.essential")
      },
      {
        id: Importance.Required,
        value: t("parts.keywords.hardFilter"),
        original: Importance.Required,
        displayElement: t("parts.keywords.hardFilter")
      }
    ],
    [t]
  );

  return (
    <div style={style}>
      {typesToDisplay.length > 0 && (
        <ul className={css.list}>
          {typesToDisplay.map((type) => (
            <TypeSection
              key={type.id}
              search={search}
              pushWithNewImportance={pushWithNewImportance}
              remove={
                canEditOrRemoveMatchableTypeValues(
                  type as unknown as ParsedSchemaProperty,
                  matchableTypes as unknown as ParsedSchemaProperty[]
                )
                  ? remove
                  : undefined
              }
              type={type}
              handleChange={
                canAddMatchableTypeValue(
                  type as unknown as ParsedSchemaProperty,
                  matchableTypes as unknown as ParsedSchemaProperty[]
                )
                  ? (option) => {
                      if (notChosen(option)) {
                        handleAddOne({ ...option.original, text: option.value });
                      }
                    }
                  : undefined
              }
              keywords={keywordsByType[type.id] || []}
              error={errors && errors[type.name]}
              initialOpen={typesOpened.includes(type.name)}
              importanceOptions={importanceOptions}
              onChangeOpen={(open) => onChangeOpen?.(open, type.name)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};
